import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import './HomePage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:7000';
const EMPTY_FORM = { name: '', email: '', password: '', role: 'practitioner' };

const ROLE_BADGE = {
  admin: { bg: '#FEF3C7', color: '#92400E', label: '🔐 Admin' },
  practitioner: { bg: '#DBEAFE', color: '#1E40AF', label: '👨‍⚕️ Practitioner' },
};

function UsersPage() {
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const token = localStorage.getItem('authToken');
  const authHeaders = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showPassword, setShowPassword] = useState(false);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [deletingId, setDeletingId] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // ── Fetch users ──────────────────────────────────────────────────────────

  const fetchUsers = useCallback(async () => {
    setIsLoading(true);
    setFetchError('');
    try {
      const res = await fetch(`${API_URL}/api/users`, { headers: authHeaders });
      if (!res.ok) throw new Error('Failed to load users');
      setUsers(await res.json());
    } catch (err) {
      setFetchError(err.message);
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  // ── Form ─────────────────────────────────────────────────────────────────

  const openForm = () => {
    setForm(EMPTY_FORM);
    setFormError('');
    setShowPassword(false);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormError('');
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!form.name.trim() || !form.email.trim() || !form.password) {
      setFormError('All fields are required.');
      return;
    }
    if (form.password.length < 6) {
      setFormError('Password must be at least 6 characters.');
      return;
    }

    setIsSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/users`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify({
          email: form.email.trim(),
          name: form.name.trim(),
          password: form.password,
          role: form.role,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to create user');
      closeForm();
      fetchUsers();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  // ── Delete ───────────────────────────────────────────────────────────────

  const handleDelete = async () => {
    if (!deletingId) return;
    setIsDeleting(true);
    try {
      const res = await fetch(`${API_URL}/api/users/${deletingId}`, {
        method: 'DELETE',
        headers: authHeaders,
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Delete failed');
      setDeletingId(null);
      fetchUsers();
    } catch (err) {
      setFetchError(err.message);
      setDeletingId(null);
    } finally {
      setIsDeleting(false);
    }
  };

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="home-shell" style={{ paddingTop: '90px' }}>

      {/* Header */}
      <header className="home-header">
        <div className="home-brand">
          <span onClick={() => navigate('/')} style={{ cursor: 'pointer', fontSize: '20px', marginRight: '4px' }}>←</span>
          <span className="home-logo-icon">👥</span>
          <span className="home-brand-name">Manage Users</span>
        </div>
        <button onClick={openForm} style={s.addBtn}>+ Create User</button>
      </header>

      {/* Title */}
      <div style={{ width: '100%', maxWidth: '860px', marginBottom: '20px' }}>
        <h1 style={{ fontSize: '26px', fontWeight: '800', color: '#1A2340', margin: '0 0 4px' }}>
          User Accounts
        </h1>
        <p style={{ fontSize: '15px', color: '#6B7280', margin: 0 }}>
          Create and manage practitioner and admin accounts.
        </p>
      </div>

      {/* Error */}
      {fetchError && (
        <div style={{ ...s.errorBox, maxWidth: '860px' }}>
          <span>⚠️</span> {fetchError}
        </div>
      )}

      {/* Loading */}
      {isLoading ? (
        <p style={{ color: '#6B7280', marginTop: '40px' }}>⏳ Loading users...</p>
      ) : (
        <div style={{ width: '100%', maxWidth: '860px' }}>
          {/* Stats row */}
          <div style={{ display: 'flex', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {[
              { label: 'Total Users', value: users.length, bg: '#EFF6FF', color: '#1E40AF' },
              { label: 'Admins',      value: users.filter(u => u.role === 'admin').length,        bg: '#FEF3C7', color: '#92400E' },
              { label: 'Practitioners', value: users.filter(u => u.role === 'practitioner').length, bg: '#F0FDF4', color: '#166534' },
            ].map(stat => (
              <div key={stat.label} style={{ background: stat.bg, borderRadius: '14px', padding: '14px 24px', minWidth: '140px' }}>
                <div style={{ fontSize: '28px', fontWeight: '900', color: stat.color }}>{stat.value}</div>
                <div style={{ fontSize: '13px', fontWeight: '600', color: stat.color, opacity: 0.8 }}>{stat.label}</div>
              </div>
            ))}
          </div>

          {/* Table */}
          <div style={s.table}>
            <div style={s.tableHead}>
              <span style={{ flex: 2 }}>Name</span>
              <span style={{ flex: 2 }}>Username</span>
              <span style={{ flex: 1 }}>Role</span>
              <span style={{ flex: 2 }}>Created</span>
              <span style={{ flex: 1, textAlign: 'right' }}>Action</span>
            </div>

            {users.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: '#9CA3AF' }}>
                No users found.
              </div>
            ) : users.map((u, idx) => {
              const badge = ROLE_BADGE[u.role] || ROLE_BADGE.practitioner;
              const isSelf = currentUser?.id === u.id;
              const date = new Date(u.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
              return (
                <div key={u.id} style={{ ...s.tableRow, background: idx % 2 === 0 ? '#fff' : '#F9FAFB' }}>
                  <span style={{ flex: 2, fontWeight: '700', color: '#1A2340' }}>
                    {u.name} {isSelf && <span style={{ fontSize: '11px', background: '#E0F2FE', color: '#0369A1', borderRadius: '6px', padding: '2px 6px', fontWeight: '700' }}>You</span>}
                  </span>
                  <span style={{ flex: 2, color: '#4B5563', fontFamily: 'monospace', fontSize: '14px' }}>{u.email}</span>
                  <span style={{ flex: 1 }}>
                    <span style={{ background: badge.bg, color: badge.color, borderRadius: '8px', padding: '3px 10px', fontSize: '12px', fontWeight: '700', whiteSpace: 'nowrap' }}>
                      {badge.label}
                    </span>
                  </span>
                  <span style={{ flex: 2, fontSize: '13px', color: '#6B7280' }}>{date}</span>
                  <span style={{ flex: 1, textAlign: 'right' }}>
                    {!isSelf ? (
                      <button onClick={() => setDeletingId(u.id)} style={s.deleteBtn} title="Delete user">🗑️</button>
                    ) : (
                      <span style={{ fontSize: '12px', color: '#D1D5DB' }}>—</span>
                    )}
                  </span>
                </div>
              );
            })}
          </div>

          <p style={{ fontSize: '13px', color: '#9CA3AF', marginTop: '12px' }}>
            {users.length} user{users.length !== 1 ? 's' : ''} total
          </p>
        </div>
      )}

      {/* ── Create User Modal ── */}
      {showForm && (
        <div style={s.overlay} onClick={closeForm}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>
            <div style={s.modalHeader}>
              <h2 style={s.modalTitle}>👤 Create New User</h2>
              <button onClick={closeForm} style={s.closeBtn}>✕</button>
            </div>

            {formError && (
              <div style={{ ...s.errorBox, marginBottom: '16px' }}>
                <span>⚠️</span> {formError}
              </div>
            )}

            <form onSubmit={handleSave} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>

              {/* Role selector — prominent at top */}
              <div style={{ display: 'flex', gap: '12px' }}>
                {['practitioner', 'admin'].map(r => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setForm(f => ({ ...f, role: r }))}
                    style={{
                      flex: 1,
                      padding: '12px',
                      borderRadius: '12px',
                      border: form.role === r ? '2px solid #2563EB' : '2px solid #E5E7EB',
                      background: form.role === r ? '#EFF6FF' : '#F9FAFB',
                      cursor: 'pointer',
                      fontWeight: '700',
                      fontSize: '14px',
                      color: form.role === r ? '#1E40AF' : '#6B7280',
                      transition: 'all 0.15s',
                    }}
                  >
                    {r === 'admin' ? '🔐 Admin' : '👨‍⚕️ Practitioner'}
                  </button>
                ))}
              </div>

              {/* Name */}
              <div style={s.field}>
                <label style={s.label}>👤 Full Name *</label>
                <input
                  type="text"
                  placeholder="e.g. Dr. Smith"
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  style={s.input}
                  disabled={isSaving}
                  autoFocus
                />
              </div>

              {/* Username */}
              <div style={s.field}>
                <label style={s.label}>🪪 Username / License ID *</label>
                <input
                  type="text"
                  placeholder="e.g. drsmith01"
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  style={s.input}
                  disabled={isSaving}
                />
              </div>

              {/* Password */}
              <div style={s.field}>
                <label style={s.label}>🔒 Password *</label>
                <div style={{ position: 'relative' }}>
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Min. 6 characters"
                    value={form.password}
                    onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
                    style={{ ...s.input, paddingRight: '44px' }}
                    disabled={isSaving}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', fontSize: '18px' }}
                  >
                    {showPassword ? '👁️' : '👁️‍🗨️'}
                  </button>
                </div>
              </div>

              {/* Info box for admin role */}
              {form.role === 'admin' && (
                <div style={{ background: '#FEF3C7', border: '1.5px solid #FCD34D', borderRadius: '10px', padding: '10px 14px', fontSize: '13px', color: '#92400E', fontWeight: '600' }}>
                  ⚠️ Admin accounts can manage hospitals, users, and all settings.
                </div>
              )}

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '4px' }}>
                <button type="button" onClick={closeForm} style={s.cancelBtn} disabled={isSaving}>Cancel</button>
                <button type="submit" style={s.saveBtn} disabled={isSaving}>
                  {isSaving ? '⏳ Creating...' : '✅ Create User'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Delete Confirm Modal ── */}
      {deletingId && (
        <div style={s.overlay} onClick={() => setDeletingId(null)}>
          <div style={{ ...s.modal, maxWidth: '400px' }} onClick={e => e.stopPropagation()}>
            <div style={{ textAlign: 'center', padding: '8px 0 8px' }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>🗑️</div>
              <h2 style={{ fontSize: '20px', fontWeight: '800', color: '#1A2340', margin: '0 0 8px' }}>Delete User?</h2>
              <p style={{ fontSize: '14px', color: '#6B7280', margin: '0 0 24px' }}>
                This will permanently remove the account. The user will no longer be able to log in.
              </p>
              <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
                <button onClick={() => setDeletingId(null)} style={s.cancelBtn} disabled={isDeleting}>Cancel</button>
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  style={{ ...s.saveBtn, background: isDeleting ? '#FCA5A5' : '#DC2626', boxShadow: 'none' }}
                >
                  {isDeleting ? '⏳ Deleting...' : '🗑️ Yes, Delete'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Styles ────────────────────────────────────────────────────────────────────

const s = {
  addBtn: {
    background: '#2563EB', color: '#fff', border: 'none', borderRadius: '12px',
    padding: '10px 20px', fontSize: '14px', fontWeight: '700', cursor: 'pointer',
    fontFamily: 'Segoe UI, Inter, sans-serif',
  },
  errorBox: {
    display: 'flex', alignItems: 'center', gap: '8px',
    background: '#FFF5F5', border: '2px solid #FEB2B2', borderRadius: '12px',
    padding: '12px 16px', fontSize: '14px', fontWeight: '600', color: '#C53030',
    boxSizing: 'border-box',
  },
  table: {
    background: '#fff', borderRadius: '16px', overflow: 'hidden',
    boxShadow: '0 2px 12px rgba(0,0,0,0.06)', border: '1px solid #E5E7EB',
  },
  tableHead: {
    display: 'flex', alignItems: 'center', padding: '13px 20px',
    background: '#1E40AF', color: '#fff', fontSize: '13px',
    fontWeight: '700', letterSpacing: '0.4px', textTransform: 'uppercase',
  },
  tableRow: {
    display: 'flex', alignItems: 'center', padding: '13px 20px',
    fontSize: '14px', borderBottom: '1px solid #F3F4F6',
  },
  deleteBtn: {
    background: '#FFF5F5', border: '1.5px solid #FEB2B2', borderRadius: '8px',
    padding: '5px 10px', fontSize: '14px', cursor: 'pointer',
  },
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200, padding: '24px',
  },
  modal: {
    background: '#fff', borderRadius: '20px', padding: '32px',
    width: '100%', maxWidth: '500px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
  },
  modalHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '24px' },
  modalTitle: { fontSize: '20px', fontWeight: '800', color: '#1A2340', margin: 0 },
  closeBtn: {
    background: '#F3F4F6', border: 'none', borderRadius: '8px',
    width: '32px', height: '32px', fontSize: '16px', cursor: 'pointer', fontWeight: '700', color: '#4B5563',
  },
  field: { display: 'flex', flexDirection: 'column', gap: '8px' },
  label: { fontSize: '14px', fontWeight: '700', color: '#374151' },
  input: {
    padding: '12px 14px', fontSize: '15px', border: '2px solid #E5E7EB',
    borderRadius: '12px', fontFamily: 'Segoe UI, Inter, sans-serif',
    color: '#1A2340', outline: 'none', boxSizing: 'border-box', width: '100%',
  },
  cancelBtn: {
    padding: '10px 20px', background: '#F3F4F6', border: '1.5px solid #D1D5DB',
    borderRadius: '10px', fontSize: '14px', fontWeight: '700', color: '#4B5563',
    cursor: 'pointer', fontFamily: 'Segoe UI, Inter, sans-serif',
  },
  saveBtn: {
    padding: '10px 24px', background: '#2563EB', border: 'none',
    borderRadius: '10px', fontSize: '14px', fontWeight: '700', color: '#fff',
    cursor: 'pointer', fontFamily: 'Segoe UI, Inter, sans-serif',
    boxShadow: '0 4px 14px rgba(37,99,235,0.3)',
  },
};

export default UsersPage;
